import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiBell, FiCheck } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMyNotifications, useUnreadNotificationCount, markNotificationAsRead } from '../../hooks/useNotifications';
import { formatRelativeTime } from '../../utils/formatters';

export const NotificationsDropdown = () => {
  const { currentUser } = useAuth();
  const { notifications } = useMyNotifications(currentUser?.uid, 5);
  const unreadCount = useUnreadNotificationCount(currentUser?.uid);
  const navigate = useNavigate();

  const unreadBg = useColorModeValue('orange.50', 'orange.900');
  const readBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }
    navigate(notification.actionUrl);
  };

  if (!currentUser) return null;

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        position="relative"
        aria-label="Notifications"
        p={2}
        minW="auto"
        _hover={{ bg: 'whiteAlpha.200' }}
        _active={{ bg: 'whiteAlpha.200' }}
      >
        <Icon as={FiBell} boxSize={5} color="gray.300" />
        {unreadCount > 0 && (
          <Badge
            position="absolute"
            top="-2px"
            right="-2px"
            colorScheme="red"
            borderRadius="full"
            fontSize="10px"
            minW="18px"
            h="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </MenuButton>
      <MenuList maxW={{ base: 'calc(100vw - 16px)', md: '360px' }} maxH="500px" overflowY="auto" p={0}>
        {/* Header */}
        <Box px={4} py={3} borderBottomWidth={1}>
          <HStack justify="space-between">
            <Text fontWeight="semibold" fontSize="sm">Notifications</Text>
            {notifications.length > 0 && (
              <Text
                as={Link}
                to="/notifications"
                fontSize="xs"
                color="orange.500"
                _hover={{ textDecoration: 'underline' }}
              >
                View all
              </Text>
            )}
          </HStack>
        </Box>

        {/* Notifications list */}
        {notifications.length === 0 ? (
          <VStack py={8} spacing={2}>
            <Icon as={FiBell} boxSize={8} color="gray.300" />
            <Text fontSize="sm" color="gray.500">No notifications yet</Text>
          </VStack>
        ) : (
          <VStack spacing={0} align="stretch" divider={<Divider />}>
            {notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                bg={notification.read ? readBg : unreadBg}
                _hover={{ bg: hoverBg }}
                p={3}
                cursor="pointer"
                whiteSpace="normal"
              >
                <HStack align="start" spacing={3} w="full">
                  {!notification.read && (
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg="orange.500"
                      flexShrink={0}
                      mt="6px"
                    />
                  )}
                  {notification.read && (
                    <Icon as={FiCheck} color="green.500" flexShrink={0} mt="2px" />
                  )}
                  <VStack align="start" spacing={1} flex="1">
                    <Text fontSize="sm" fontWeight={notification.read ? 'normal' : 'semibold'} lineHeight="short">
                      {notification.title}
                    </Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={2} lineHeight="short">
                      {notification.message}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      {formatRelativeTime(notification.createdAt)}
                    </Text>
                  </VStack>
                </HStack>
              </MenuItem>
            ))}
          </VStack>
        )}
      </MenuList>
    </Menu>
  );
};
